import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Clock, Zap, ImageIcon, PenLine, Check, X as XIcon,
  ChevronRight, Plus, Minus, Film, Layers, ArrowRight, LogIn,
} from "lucide-react";

// LANDING — conversion-first redesign (2026-05-13)
// Positioning shift: from "tool to create reels" to "machine that brings
// customers". Hero promises an outcome, not a feature. Every section maps
// to a buyer objection (skill, time, ideas, cost).

const ASSETS = {
  reel_frame:
    "https://static.prod-images.emergentagent.com/jobs/34c574ca-4d73-468f-bbf7-ee67b0d6e75e/images/f37a248f08841225aa2796477c118ed06d05b448de5c0faceea0c105f9805a15.png",
  carousel_slide:
    "https://static.prod-images.emergentagent.com/jobs/34c574ca-4d73-468f-bbf7-ee67b0d6e75e/images/82f9741ced5a2ff6526327db7da3dd445581df4262bae08bfe844c54223cdfd8.png",
  raw_photo:
    "https://static.prod-images.emergentagent.com/jobs/34c574ca-4d73-468f-bbf7-ee67b0d6e75e/images/f6c09b658bd143a5ee54ab93d79d3df3ea2f8eec3b0eb0ef0374d6214c2624c7.png",
  iphone_mockup:
    "https://static.prod-images.emergentagent.com/jobs/34c574ca-4d73-468f-bbf7-ee67b0d6e75e/images/0ce3ebd6a489e47f91e26b4335ca8efcbc6a6ba5c156fd05381ead6a571c1278.png",
};

// Fallback content for the Showcase section — shown when the real-content
// API is unavailable (e.g. fresh production deploy with an empty DB) or
// returns zero items. These point to safe public stock photos so we never
// render an empty grid that screams "this product has no users".
const FALLBACK_SHOWCASE_ITEMS = [
  {
    type: "reel", id: "demo-reel-1",
    title: "3 errori da evitare nel mutuo",
    badge: "Reel · 9:16",
    src: "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[9/16]",
  },
  {
    type: "carousel", id: "demo-car-1",
    title: "Risparmi centinaia di euro al mese",
    badge: "Carosello · 6 slide",
    src: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[4/5]",
  },
  {
    type: "reel", id: "demo-reel-2",
    title: "Lancio menù primavera al ristorante",
    badge: "Reel · 9:16",
    src: "https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[9/16]",
  },
  {
    type: "carousel", id: "demo-car-2",
    title: "5 segreti del marketing locale",
    badge: "Carosello · 5 slide",
    src: "https://images.pexels.com/photos/4068314/pexels-photo-4068314.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[4/5]",
  },
  {
    type: "reel", id: "demo-reel-3",
    title: "Vendi casa in 48h",
    badge: "Reel · 9:16",
    src: "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[9/16]",
  },
  {
    type: "carousel", id: "demo-car-3",
    title: "Come scegliere il tasso giusto",
    badge: "Carosello · 4 slide",
    src: "https://images.pexels.com/photos/210600/pexels-photo-210600.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[4/5]",
  },
  {
    type: "reel", id: "demo-reel-4",
    title: "Allenamento per chi non ha tempo",
    badge: "Reel · 9:16",
    src: "https://images.pexels.com/photos/116078/pexels-photo-116078.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[9/16]",
  },
  {
    type: "carousel", id: "demo-car-4",
    title: "3 errori finanziari del 2026",
    badge: "Carosello · 4 slide",
    src: "https://images.pexels.com/photos/2068975/pexels-photo-2068975.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[4/5]",
  },
  {
    type: "reel", id: "demo-reel-5",
    title: "La promo del weekend in barbershop",
    badge: "Reel · 9:16",
    src: "https://images.pexels.com/photos/1813272/pexels-photo-1813272.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[9/16]",
  },
  {
    type: "carousel", id: "demo-car-5",
    title: "Come acquisire clienti su Instagram",
    badge: "Carosello · 6 slide",
    src: "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg?auto=compress&cs=tinysrgb&w=600",
    ratio: "aspect-[4/5]",
  },
];

// ─────────────────────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────────────────────
const Header = () => (
  <header className="fixed w-full top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-zinc-100">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between gap-4">
      <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2.5 shrink-0">
        <img src="/reelio-icon-transparent.png" alt="" className="h-8 w-auto" />
        <span className="font-display font-black text-xl tracking-tighter text-zinc-900">
          Reelio
        </span>
      </Link>
      <nav className="hidden md:flex items-center gap-7 text-sm font-body text-zinc-600">
        <a href="#how" className="hover:text-zinc-900 transition-colors" data-testid="header-nav-how">Come funziona</a>
        <a href="#showcase" className="hover:text-zinc-900 transition-colors" data-testid="header-nav-showcase">Esempi</a>
        <a href="#faq" className="hover:text-zinc-900 transition-colors" data-testid="header-nav-faq">FAQ</a>
      </nav>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <Link
          to="/login"
          data-testid="header-login-button"
          className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 border border-zinc-300 hover:border-zinc-900 text-zinc-800 hover:text-zinc-900 text-sm font-display font-bold rounded-md transition-colors"
        >
          <LogIn className="w-3.5 h-3.5" />
          Accedi
        </Link>
        <Link
          to="/register"
          data-testid="header-register-button"
          className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] text-white text-sm font-display font-bold rounded-md transition-colors"
        >
          Prova gratis
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  </header>
);

// ─────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────
const Hero = () => (
  <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 px-6 sm:px-8 bg-[#FDFBF7] overflow-hidden">
    {/* Soft violet/coral blobs */}
    <div className="pointer-events-none absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-violet-300/30 blur-3xl" />
    <div className="pointer-events-none absolute top-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#FF4400]/15 blur-3xl" />

    <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 border border-violet-200 bg-violet-50 rounded-full">
          <Sparkles className="w-3.5 h-3.5 text-violet-600" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-700">
            La macchina genera-clienti
          </span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-900 leading-[1.02]">
          Trasforma{" "}
          <span className="relative whitespace-nowrap">
            <span className="relative z-10">foto e idee</span>
            <span className="absolute inset-x-0 bottom-1 h-3 bg-[#FF4400]/25 -skew-y-1 z-0" />
          </span>{" "}
          in contenuti che{" "}
          <span className="text-[#FF4400]">attirano clienti</span>.
        </h1>
        <p className="mt-7 text-lg sm:text-xl text-zinc-600 font-body leading-relaxed max-w-xl">
          L&apos;IA che trasforma i tuoi pensieri in reel e caroselli pronti da pubblicare.
          Nessuna competenza di editing richiesta. Inizi a generare clienti in <strong className="text-zinc-900">60 secondi</strong>.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3">
          <Link
            to="/register"
            data-testid="hero-register-button"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#FF4400] hover:bg-[#FF6633] text-white text-base font-display font-bold rounded-md transition-all shadow-lg shadow-[#FF4400]/25 hover:shadow-xl hover:shadow-[#FF4400]/30 hover:-translate-y-0.5"
          >
            <Sparkles className="w-4 h-4" />
            Prova gratis
            <ChevronRight className="w-4 h-4" />
          </Link>
          <a
            href="#showcase"
            data-testid="hero-showcase-link"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-zinc-300 hover:border-zinc-400 text-zinc-700 hover:text-zinc-900 text-base font-body font-medium rounded-md transition-colors"
          >
            Vedi esempi reali
          </a>
        </div>
        <div className="mt-7 flex items-center gap-5 text-xs font-mono uppercase tracking-widest text-zinc-500">
          <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" /> 30 giorni gratis</div>
          <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" /> Nessuna carta</div>
        </div>
      </div>

      {/* Phone mockup with REAL reel content inside the screen */}
      <div className="relative mx-auto w-full max-w-[340px]">
        <div className="relative iphone-float">
          {/* Glow underneath */}
          <div className="absolute -inset-6 bg-gradient-to-br from-[#FF4400]/20 via-violet-300/30 to-[#FF4400]/10 blur-2xl rounded-full" />

          {/* Phone frame */}
          <div className="relative aspect-[9/19] mx-auto rounded-[2.5rem] bg-zinc-900 p-2.5 shadow-2xl ring-1 ring-zinc-800/30">
            {/* Notch */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 h-6 w-28 bg-zinc-900 rounded-b-2xl flex items-center justify-center">
              <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
            </div>
            {/* Screen */}
            <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-zinc-950">
              <img
                src={ASSETS.reel_frame}
                alt="Esempio di reel generato con Reelio"
                className="w-full h-full object-cover"
                data-testid="hero-phone-screen-image"
              />
              {/* Reel-style overlay (caption + play indicator) */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/30 to-transparent">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#FF4400] flex items-center justify-center">
                    <span className="font-display font-black text-white text-[10px]">R</span>
                  </div>
                  <div className="text-white text-xs font-display font-bold">@tuobrand</div>
                </div>
                <div className="text-white font-display font-bold text-sm leading-tight">
                  3 errori da evitare con il mutuo
                </div>
                <div className="text-white/70 text-[10px] font-body mt-1">
                  #mutuo · #consulenza
                </div>
              </div>
              {/* Vertical reel UI hint */}
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur text-white text-[9px] font-mono uppercase tracking-widest">
                Reel · 9:16
              </div>
            </div>
          </div>
        </div>
        {/* "Generated in" floating chip */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-zinc-900 text-white text-xs font-mono uppercase tracking-widest rounded-full shadow-xl flex items-center gap-2 z-20 whitespace-nowrap">
          <Clock className="w-3 h-3" /> Reel generato in 47s
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// SPEED PROOF (stats strip)
// ─────────────────────────────────────────────────────────────
const SpeedProof = () => (
  <section className="py-16 bg-white border-y border-zinc-100" data-testid="speedproof-section">
    <div className="max-w-6xl mx-auto px-6 sm:px-8 grid grid-cols-3 gap-6 text-center">
      {[
        { val: "1.000+", label: "Reel pubblicati" },
        { val: "60s", label: "Tempo di creazione" },
        { val: "0", label: "Competenze richieste" },
      ].map((s) => (
        <div key={s.label}>
          <div className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-violet-600">
            {s.val}
          </div>
          <div className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// BEFORE / AFTER — two transformations side by side
// ─────────────────────────────────────────────────────────────
const TransformCard = ({ icon: Icon, beforeLabel, afterLabel, beforeImg, afterImg, beforeTone = "photo", testId }) => (
  <div className="relative bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow" data-testid={testId}>
    <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 bg-violet-600 text-white text-[10px] font-mono uppercase tracking-widest rounded-full shadow-sm">
      <Icon className="w-3 h-3" /> Trasformazione
    </div>

    <div className="grid grid-cols-[1fr_auto_1fr] gap-4 sm:gap-5 items-center">
      {/* BEFORE */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Prima</div>
        {beforeTone === "text" ? (
          <div className="aspect-[4/5] rounded-xl bg-zinc-50 border border-zinc-200 flex items-center justify-center px-4 text-center">
            <p className="font-body italic text-sm text-zinc-500 leading-snug">
              &ldquo;Voglio promuovere il mio<br />nuovo menu primavera&rdquo;
            </p>
          </div>
        ) : (
          <img src={beforeImg} alt={beforeLabel} className="aspect-[4/5] rounded-xl object-cover w-full grayscale-[0.3]" />
        )}
        <div className="mt-2 text-xs font-body text-zinc-500">{beforeLabel}</div>
      </div>

      {/* ARROW */}
      <div className="flex flex-col items-center justify-center text-violet-600">
        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
        <span className="font-mono text-[8px] sm:text-[9px] mt-1 uppercase tracking-widest text-violet-500">60s</span>
      </div>

      {/* AFTER */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-violet-600 mb-2">Dopo</div>
        <div className="relative">
          <img src={afterImg} alt={afterLabel} className="aspect-[9/16] sm:aspect-[4/5] rounded-xl object-cover w-full shadow-md" />
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-[8px] font-mono uppercase tracking-widest rounded shadow">
            <Check className="w-2.5 h-2.5" /> Pronto
          </div>
        </div>
        <div className="mt-2 text-xs font-body text-zinc-700 font-semibold">{afterLabel}</div>
      </div>
    </div>
  </div>
);

const BeforeAfter = () => (
  <section id="how" className="py-24 sm:py-32 px-6 sm:px-8 bg-[#FDFBF7]">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-600 mb-4">
          · Il potere dell&apos;automazione
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900">
          Da idea grezza a contenuto virale.
        </h2>
        <p className="mt-4 text-zinc-600 font-body max-w-2xl mx-auto">
          Due strade per arrivare allo stesso posto: un contenuto pronto da pubblicare. Tu scegli da dove parti.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
        <TransformCard
          icon={ImageIcon}
          beforeLabel="Foto grezza del cliente"
          afterLabel="Reel pubblicato"
          beforeImg={ASSETS.raw_photo}
          afterImg={ASSETS.reel_frame}
          testId="transform-card-photo"
        />
        <TransformCard
          icon={PenLine}
          beforeLabel="1 riga di testo"
          afterLabel="Reel + Carosello pronti"
          beforeTone="text"
          afterImg={ASSETS.carousel_slide}
          testId="transform-card-text"
        />
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// SKILL BARRIER — comparison
// ─────────────────────────────────────────────────────────────
const ComparisonColumn = ({ tone, title, time, points, testId }) => {
  const bad = tone === "without";
  return (
    <div
      data-testid={testId}
      className={
        bad
          ? "relative rounded-2xl p-6 sm:p-8 bg-zinc-100 border border-zinc-200 grayscale-[0.4]"
          : "relative rounded-2xl p-6 sm:p-8 bg-white border-2 border-[#FF4400] shadow-xl shadow-[#FF4400]/10"
      }
    >
      {!bad && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 bg-[#FF4400] text-white text-[10px] font-mono uppercase tracking-widest rounded-full shadow-sm">
          <Sparkles className="w-3 h-3" /> Reelio
        </div>
      )}
      <div className="flex items-baseline gap-3 mb-4">
        <h3 className={bad ? "font-display font-bold text-xl text-zinc-500" : "font-display font-black text-xl text-zinc-900"}>
          {title}
        </h3>
        <span className={bad ? "font-mono text-xs uppercase tracking-widest text-zinc-400" : "font-mono text-xs uppercase tracking-widest text-[#FF4400]"}>
          {time}
        </span>
      </div>
      <ul className="space-y-3">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2.5">
            {bad ? (
              <XIcon className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
            ) : (
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            )}
            <span className={bad ? "text-sm text-zinc-500 font-body line-through decoration-zinc-300" : "text-sm text-zinc-800 font-body"}>
              {p}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SkillBarrier = () => (
  <section className="py-24 sm:py-32 px-6 sm:px-8 bg-white">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-4">
          · Smetti di perdere tempo
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900">
          Non serve saper editare video.
        </h2>
        <p className="mt-4 text-zinc-600 font-body max-w-2xl mx-auto">
          Dimentica Premiere e CapCut. Dimentica le notti a cercare idee. Reelio fa il lavoro sporco al posto tuo.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        <ComparisonColumn
          tone="without"
          title="Senza Reelio"
          time="ore"
          testId="comparison-without"
          points={[
            "Ore su Premiere/CapCut",
            "Ricerca disperata di idee",
            "Pubblicazione incostante",
            "Frustrazione tecnica",
          ]}
        />
        <ComparisonColumn
          tone="with"
          title="Con Reelio"
          time="60 secondi"
          testId="comparison-with"
          points={[
            "60 secondi a contenuto",
            "Idee illimitate generate dall'IA",
            "Pubblicazione costante e ordinata",
            "Più clienti, meno stress",
          ]}
        />
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// SHOWCASE — real outputs grid
// ─────────────────────────────────────────────────────────────
const Showcase = () => {
  // Static showcase: the founder explicitly asked NOT to pull from their own
  // DB. We always render the curated set so the page looks consistent and
  // intentional across preview and production.
  const items = FALLBACK_SHOWCASE_ITEMS.slice(0, 10);

  return (
    <section id="showcase" className="py-24 sm:py-32 px-6 sm:px-8 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-600 mb-4">
            · Risultati reali
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900">
            Contenuti pubblicabili. Senza ritocchi.
          </h2>
          <p className="mt-4 text-zinc-600 font-body max-w-2xl mx-auto">
            Esempi reali generati con Reelio. Tutto quello che vedi qui sotto è stato creato in meno di 60 secondi a partire da una sola riga di testo.
          </p>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5"
          data-testid="showcase-grid"
        >
          {items.map((it, i) => {
            const IconC = it.type === "reel" ? Film : Layers;
            return (
              <Link
                key={`${it.type}-${it.id}`}
                to="/register"
                className={`group relative ${it.ratio} rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all bg-zinc-100 block`}
                data-testid={`showcase-item-${i}`}
              >
                <img
                  src={it.src}
                  alt={it.title || it.badge}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 bg-white/95 backdrop-blur text-zinc-900 text-[9px] font-mono uppercase tracking-widest rounded">
                  <IconC className="w-2.5 h-2.5" />
                  {it.badge}
                </div>
                {/* Hover overlay: pushes the visitor toward signup */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white text-zinc-900 font-display font-bold text-xs rounded-md shadow-lg">
                    <Sparkles className="w-3.5 h-3.5 text-[#FF4400]" />
                    Crea il tuo
                    <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
                {it.title && (
                  <div className="absolute bottom-0 inset-x-0 p-3">
                    <div className="font-display font-black text-white text-sm leading-tight tracking-tight line-clamp-2">
                      {it.title}
                    </div>
                  </div>
                )}
              </Link>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/register"
            data-testid="showcase-cta-button"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-zinc-900 hover:bg-zinc-800 text-white text-base font-display font-bold rounded-md transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Genera il tuo primo contenuto
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// PRICING TEASER
// ─────────────────────────────────────────────────────────────
const PricingTeaser = () => (
  <section className="py-24 sm:py-28 px-6 sm:px-8 bg-white">
    <div className="max-w-3xl mx-auto text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-4">
        · Inizia a crescere oggi
      </div>
      <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-zinc-900">
        Prova tutte le funzionalità per 30 giorni.
      </h2>
      <p className="mt-5 text-lg text-zinc-600 font-body">
        Poi piani a partire da <strong className="text-zinc-900">10€/mese</strong>. Niente carta richiesta per la prova.
      </p>
      <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/register"
          data-testid="pricing-register-button"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF4400] hover:bg-[#FF6633] text-white text-base font-display font-bold rounded-md transition-all shadow-lg shadow-[#FF4400]/25 hover:shadow-xl hover:-translate-y-0.5"
        >
          <Sparkles className="w-4 h-4" />
          Prova gratis 30 giorni
          <ChevronRight className="w-4 h-4" />
        </Link>
        <Link
          to="/pricing"
          data-testid="pricing-details-link"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-zinc-300 hover:border-zinc-400 text-zinc-700 hover:text-zinc-900 font-body font-medium rounded-md transition-colors"
        >
          Vedi tutti i piani
        </Link>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────
const FAQItem = ({ q, a, index, open, onToggle }) => (
  <div className="border-b border-zinc-200" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      data-testid={`faq-trigger-${index}`}
      className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      aria-expanded={open}
    >
      <span className="font-display font-bold text-base sm:text-lg text-zinc-900">{q}</span>
      {open ? <Minus className="w-4 h-4 text-zinc-500 shrink-0" /> : <Plus className="w-4 h-4 text-[#FF4400] shrink-0" />}
    </button>
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
    >
      <div className="overflow-hidden">
        <p className="pb-5 text-zinc-600 font-body leading-relaxed">{a}</p>
      </div>
    </div>
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const items = [
    {
      q: "Non so editare video. È un problema?",
      a: "Assolutamente no. Reelio fa tutto il lavoro sporco. Tu metti l'idea o una foto, l'IA crea il video completo di transizioni, scritte e ritmo. Bastano un paio di click.",
    },
    {
      q: "Sono troppo impegnato. Quanto tempo richiede?",
      a: "Meno di 60 secondi. È progettato specificamente per imprenditori che non hanno tempo da perdere sui social. Apri, scrivi l'idea, esporta.",
    },
    {
      q: "Non ho idee per i contenuti. Come faccio?",
      a: "L'IA suggerisce idee virali basate sul tuo settore. Tu scegli da una lista, Reelio scrive lo script, genera le immagini e monta il video.",
    },
    {
      q: "Costa troppo?",
      a: "Inizia gratis per 30 giorni, nessuna carta richiesta. Poi a partire da soli 10€/mese — costa meno di un caffè al giorno per avere una macchina genera-clienti sempre attiva.",
    },
    {
      q: "Posso usarlo per la mia attività specifica?",
      a: "Sì. Reelio funziona per ristoranti, palestre, e-commerce, consulenti, immobiliare, parrucchieri, hotel e qualsiasi attività che cerca clienti via social.",
    },
    {
      q: "I contenuti sono davvero pubblicabili?",
      a: "Sì. Ogni reel esce in MP4 verticale 9:16 e ogni carosello in PNG 4:5 — i formati nativi di Instagram, TikTok e Facebook. Scarichi e pubblichi.",
    },
  ];
  return (
    <section id="faq" className="py-24 sm:py-28 px-6 sm:px-8 bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-600 mb-4">
            · Domande frequenti
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-zinc-900">
            Quello che ci chiedono sempre.
          </h2>
        </div>
        <div className="bg-white rounded-2xl px-6 sm:px-8 shadow-sm" data-testid="faq-list">
          {items.map((it, i) => (
            <FAQItem
              key={i}
              index={i}
              q={it.q}
              a={it.a}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// FINAL CTA
// ─────────────────────────────────────────────────────────────
const FinalCTA = () => (
  <section className="py-24 sm:py-28 px-6 sm:px-8 bg-zinc-900 text-white">
    <div className="max-w-4xl mx-auto text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-4">
        · Last call
      </div>
      <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
        La tua attività sui social.<br />
        <span className="text-[#FF4400]">Anche se non sai fare video.</span>
      </h2>
      <p className="mt-6 text-lg text-zinc-300 font-body max-w-2xl mx-auto">
        Trasforma foto e idee in contenuti che attirano clienti. 60 secondi alla volta.
      </p>
      <div className="mt-9">
        <Link
          to="/register"
          data-testid="footer-cta-register"
          className="inline-flex items-center gap-2 px-9 py-4 bg-[#FF4400] hover:bg-[#FF6633] text-white text-lg font-display font-bold rounded-md transition-all shadow-2xl shadow-[#FF4400]/30 hover:-translate-y-0.5"
        >
          <Sparkles className="w-5 h-5" />
          Prova gratis
          <ChevronRight className="w-5 h-5" />
        </Link>
        <div className="mt-5 flex justify-center items-center gap-6 text-xs font-mono uppercase tracking-widest text-zinc-400">
          <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> 30 giorni gratis</span>
          <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" /> Nessuna carta</span>
          <span className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-yellow-400" /> 60s al primo reel</span>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="py-12 bg-white border-t border-zinc-100">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <img src="/reelio-icon-transparent.png" alt="" className="h-7 w-auto" />
          <span className="font-display font-black text-lg tracking-tighter text-zinc-900">Reelio</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-body text-zinc-500">
          <Link to="/pricing" className="hover:text-zinc-900 transition-colors" data-testid="footer-pricing-link">Prezzi</Link>
          <Link to="/login" className="hover:text-zinc-900 transition-colors" data-testid="footer-login-link">Accedi</Link>
          <a href="#faq" className="hover:text-zinc-900 transition-colors" data-testid="footer-faq-link">FAQ</a>
          <Link to="/privacy" className="hover:text-zinc-900 transition-colors" data-testid="footer-privacy">Privacy</Link>
          <Link to="/cookie-policy" className="hover:text-zinc-900 transition-colors" data-testid="footer-cookies">Cookie</Link>
          <Link to="/terms" className="hover:text-zinc-900 transition-colors" data-testid="footer-terms">Termini</Link>
          <Link to="/contact" className="hover:text-zinc-900 transition-colors" data-testid="footer-contact">Contatti</Link>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 pt-4 border-t border-zinc-100">
        <div>© 2026 Reelio · Marco Marangoni · P.IVA 04018220964</div>
        <div>Tutti i diritti riservati</div>
      </div>
    </div>
  </footer>
);

// ─────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────
export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-violet-200 selection:text-zinc-900">
      <style>{`
        @keyframes iphoneFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .iphone-float { animation: iphoneFloat 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .iphone-float { animation: none; } }
      `}</style>
      <Header />
      <main>
        <Hero />
        <SpeedProof />
        <BeforeAfter />
        <SkillBarrier />
        <Showcase />
        <PricingTeaser />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
